import React from 'react'
import styled from 'styled-components'
import humanizeDuration from 'humanize-duration'
import {
	VictoryChart,
	VictoryLine,
	VictoryAxis,
	VictoryTheme,
	VictoryArea,
} from 'victory'
import Text from '../shared/Text'
import styles from '../../utility/styles'
import { formatNumber, isNullOrUndefined, returnTimeString } from '../../utility/helpers'
import {
	CarIcon,
	ChargeCardArrow,
	ChargeCardBattery,
	ChargeCardCarbon,
	ChargeCardRegen,
} from '../../utility/icons'

const Wrapper = styled.div`
	background-color: var(--tz-surface-raised, #1A202C);
	border-radius: 1px;
	padding: 12px 16px;
	margin-right: 32px;
`

const StyledRow = styled.div`
	display: flex;
	align-items: center;
`

const LargeRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex: 1;
`

const CarTypeContainer = styled.div`
	display: flex;
	padding: 2px 6px;
	align-items: center;
	gap: 8px;
	border-radius: 2px;
	background-color: ${styles.colors.mediumGray};
`

const FuckingAxisRow = styled.div`
	margin: -10px 50px 12px 50px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	@media only screen and (max-width: 450px) {
		margin: 0;
		
	}
`

const ChartWrapper = styled.div`
	width: 450px;
	@media only screen and ${styles.breakpoints.tablet} {
		& > .VictoryContainer {
			user-select: auto !important;
        	pointer-events: auto !important;
        	touch-action: auto !important;
		}
	}
	@media only screen and ${styles.breakpoints.mobile} {
		width: 75dvw;
	}
`

const LegendRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
`

const PowerLegend = styled.div`
	width: 35px;
	height: 2px;
	margin-right: 8px;
	border-radius: 100px;
	background: linear-gradient(
		90deg,
		${styles.colors.powerColorRange[2]} 0%,
		${styles.colors.powerColorRange[1]} 49.59%,
		${styles.colors.powerColorRange[0]} 100%
	);
`

const ChargeLegend = styled.div`
	width: 16px;
	height: 2px;
	margin-right: 8px;
	background-color: ${styles.colors.chargingGreen};
`

// Bar

const BarBackground = styled.div`
	background-color: ${styles.colors.background};
	height: 8px;
	width: 100%;
	position: relative;
	border-radius: 1px;
	margin-bottom: 24px;
`

const BarCharge = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${({ startPct }) => startPct}%;
	width: ${({ startPct, endPct }) => endPct - startPct}%;
	background-color: ${styles.colors.chargingGreen};
`

const BarFaded = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	width: ${({ startPct }) => startPct}%;
	background-color: ${styles.functions.opacityModifier(
		styles.colors.chargingGreen,
		32
	)};
`

// helpers
const returnStops = powerMax => {
	if (powerMax > 50) {
		const zero = {
			offset: 0,
			stopColor: styles.colors.powerColorRange[0],
			stopOpacity: 1,
		}
		const one = {
			offset: 0.5,
			stopColor: styles.colors.powerColorRange[1],
			stopOpacity: 1,
		}
		const two = {
			offset: 1,
			stopColor: styles.functions.getChargePowerColor(powerMax),
			stopOpacity: 1,
		}
		return [zero, one, two]
	}
	return [
		{
			offset: 0,
			stopColor: styles.colors.powerColorRange[0],
			stopOpacity: 1,
		},
		{
			offset: 1,
			stopColor: styles.functions.getChargePowerColor(powerMax),
			stopOpacity: 1,
		},
	]
}

const returnPowerStops = powerMax => {
	if (powerMax > 50) {
		const zero = {
			offset: 0,
			stopColor: styles.colors.powerColorRange[0],
			stopOpacity: 0,
		}
		const one = {
			offset: 0.5,
			stopColor: styles.colors.powerColorRange[1],
			stopOpacity: 0.35,
		}
		const two = {
			offset: 1,
			stopColor: styles.functions.getChargePowerColor(powerMax),
			stopOpacity: 0.7,
		}
		return [zero, one, two]
	}
	return [
		{
			offset: 0,
			stopColor: styles.colors.powerColorRange[0],
			stopOpacity: 0,
		},
		{
			offset: 1,
			stopColor: styles.functions.getChargePowerColor(powerMax),
			stopOpacity: 0.7,
		},
	]
}

/**
 *
 * @param {{
 *   startTs:number,
 *   endTs:number,
 *   duration:number,
 *   startRange:number,
 *   endRange:number,
 *   startPct:number,
 *   endPct:number,
 *   carbon:number,
 *   carbonRelative:number,
 *   rangeDiff:number,
 *   chartData:{t:number,p:number,l:number}[],
 *   carType:string,
 *   power:number,
 *   style:React.CSSProperties
 * }} props
 */
export default function RecentChargeCard({
	startTs,
	endTs,
	duration,
	startRange,
	endRange,
	carbon,
	carbonRelative,
	startPct,
	endPct,
	rangeDiff,
	chartData = [],
	carType,
	power,
	style,
}) {
	const powerMax = React.useMemo(() => {
		const sorted = [...chartData].sort((a, b) => b.p - a.p)
		return sorted[0]?.p ?? 0
	}, [chartData])
	const stops = React.useMemo(() => returnStops(powerMax), [powerMax])
	const powerStops = React.useMemo(
		() => returnPowerStops(powerMax),
		[powerMax]
	)
	const carbonString = React.useMemo(() => {
		if (isNullOrUndefined(carbon)) return ''
		if (carbon < 1000) return `${carbon}g`
		return `${formatNumber((carbon / 1000).toFixed(1))}kg`
	}, [carbon])
	const color = React.useMemo(
		() => styles.functions.getCarbonColorFromPercent(carbonRelative),
		[carbonRelative]
	)
	const start = React.useMemo(
		() => returnTimeString(startTs, { format: 'h:mma' }),
		[startTs]
	)
	const end = React.useMemo(
		() => returnTimeString(endTs, { format: 'h:mma' }),
		[endTs]
	)
	return (
		<Wrapper {...{ style }}>
			<LargeRow>
				<Text.Body variant='bold'>
					{returnTimeString(startTs, { format: 'MMM DD, YYYY' })}
				</Text.Body>
				<CarTypeContainer>
					<CarIcon />
					<Text.Tiny variant='bold'>{carType}</Text.Tiny>
				</CarTypeContainer>
			</LargeRow>
			<StyledRow style={{ marginBottom: '4px' }}>
				<Text.Tiny>
					{start} •{' '}
					{humanizeDuration(duration * 1000, {
						units: ['d', 'h', 'm'],
						round: true,
					})}
				</Text.Tiny>
				<ChargeCardBattery style={{ margin: '0 4px' }} />
				<Text.Tiny>{startRange}</Text.Tiny>
				<ChargeCardArrow style={{ margin: '0 4px' }} />
				<Text.Tiny>{endRange}</Text.Tiny>
			</StyledRow>
			<BarBackground>
				<BarFaded {...{ startPct }} />
				<BarCharge {...{ startPct, endPct }} />
			</BarBackground>
			<LargeRow>
				{Boolean(carbonString) && (
					<StyledRow style={{ flex: 1 }}>
						<ChargeCardCarbon
							fill={color}
							style={{ marginRight: '4px' }}
						/>
						<Text.Caption variant='bold' style={{ color }}>
							{carbonString}
						</Text.Caption>
					</StyledRow>
				)}
				<StyledRow style={{ flex: 1 }}>
					<ChargeCardRegen style={{ marginRight: '4px' }} />
					<Text.Caption variant='bold'>{rangeDiff}</Text.Caption>
				</StyledRow>
			</LargeRow>
			<ChartWrapper>
				<VictoryChart
					domain={{ y: [0, 1.1] }}
					height={250}
					padding={{ top: 20, bottom: 20, left: 50, right: 50 }}
				>
					<defs key='gradient'>
						<linearGradient
							id={`gradientStroke-ts=${startTs}`}
							x1={0}
							x2={0}
							y1={1}
							y2={0}
						>
							{stops.map(
								({ offset, stopColor, stopOpacity }, i) => (
									<stop
										key={`gradientStroke-stop-${i + 1}`}
										{...{ offset, stopColor, stopOpacity }}
									/>
								)
							)}
						</linearGradient>
						<linearGradient
							id={`powerGradient-ts=${startTs}`}
							x1={0}
							x2={0}
							y1={1}
							y2={0}
						>
							{powerStops.map(
								({ offset, stopColor, stopOpacity }, i) => (
									<stop
										key={`powerGradient-stop-${i + 1}`}
										{...{ offset, stopColor, stopOpacity }}
									/>
								)
							)}
						</linearGradient>
						<linearGradient
							x1={0}
							x2={0}
							y1={0}
							y2={1}
							id='chargeGradient'
						>
							<stop
								offset='0%'
								stopColor={styles.colors.chargingGreen}
								stopOpacity={0.7}
							/>
							<stop
								offset='100%'
								stopColor={styles.colors.chargingGreen}
								stopOpacity={0}
							/>
						</linearGradient>
					</defs>
					<VictoryLine
						data={chartData}
						y={({ l }) => l / 100}
						x={({ t }) => t}
						style={{
							data: {
								stroke: styles.colors.chargingGreen,
								strokeWidth: 3,
							},
						}}
					/>
					<VictoryArea
						data={chartData}
						y={({ l }) => l / 100}
						x={({ t }) => t}
						interpolation='natural'
						style={{
							data: {
								fill: `url(#chargeGradient)`,
							},
						}}
					/>
					<VictoryArea
						data={chartData}
						interpolation='natural'
						y={({ p }) => p / power}
						x={({ t }) => t}
						style={{
							data: {
								fill: `url(#powerGradient-ts=${startTs})`,
							},
						}}
					/>
					<VictoryLine
						data={chartData}
						interpolation='natural'
						y={({ p }) => p / power}
						x={({ t }) => t}
						style={{
							data: {
								stroke: `url(#gradientStroke-ts=${startTs})`,
								strokeWidth: 3,
							},
						}}
					/>
					<VictoryAxis
						style={{
							tickLabels: { opacity: 0 },
							ticks: { opacity: 0 },
							axis: {
								stroke: styles.colors.mediumGray,
								strokeWidth: 1.5,
							},
						}}
					/>
					<VictoryAxis
						dependentAxis
						style={{
							tickLabels: {
								fill: styles.colors.lightGray,
								fontFamily: styles.fonts.default,
								fontSize: 12,
								padding: 5,
							},
							axis: {
								stroke: 'transparent',
							},
							grid: {
								stroke: styles.colors.lightGray,
								strokeDasharray: ({ tick }) =>
									tick === 0 ? '0' : '5 5',
							},
							ticks: {
								stroke: styles.colors.lightGray,
								size: 0,
								strokeWidth: 2,
							},
						}}
						tickValues={[0, 0.25, 0.5, 0.75, 1]}
						tickFormat={tick => `${tick * 100}%`}
					/>
					<VictoryAxis
						dependentAxis
						style={{
							tickLabels: {
								fill: styles.colors.lightGray,
								fontFamily: styles.fonts.normal,
								fontSize: 12,
								padding: 5,
							},
							axis: {
								stroke: 'transparent',
							},
							ticks: {
								stroke: styles.colors.lightGray,
								size: 0,
							},
						}}
						orientation='right'
						tickValues={[0, 0.25, 0.5, 0.75, 1]}
						tickFormat={(tick, i) =>
							powerMax > 4
								? `${Math.round(tick * power)}kW`
								: `${(tick * power).toFixed(1)}kW`
						}
					/>
				</VictoryChart>
			</ChartWrapper>
			<FuckingAxisRow>
				<Text.Tiny variant='bold' color={styles.colors.lightGray}>
					{start}
				</Text.Tiny>
				<Text.Tiny variant='bold' color={styles.colors.lightGray}>
					{end}
				</Text.Tiny>
			</FuckingAxisRow>
			<LegendRow>
				<StyledRow style={{ margin: '0 12px' }}>
					<ChargeLegend />
					<Text.Tiny variant='bold'>Battery (SOC)</Text.Tiny>
				</StyledRow>
				<StyledRow style={{ margin: '0 12px' }}>
					<PowerLegend />
					<Text.Tiny variant='bold'>Power</Text.Tiny>
				</StyledRow>
			</LegendRow>
		</Wrapper>
	)
}
